import { deepClone, getEventName } from './utils';
import { changeRateBackToNewContract, changeRateBackToSelection, changeRateStepApprovalFlagsSubmit, changeRateStepChange, changeRateStepDeliveryDateSubmit, changeRateStepperEnter, changeRateSubmit, orderBackToSelection, orderStepApprovalFlagsSubmit, orderStepChange, orderStepCustomerAccountSelectionSubmit, orderStepDeliveryAddressSubmit, orderStepPaymentMethodSubmit, orderStepperEnter, orderStepPersonalDataSubmit, orderStepSupplyConnectionSubmit, orderSubmit, orderToChangeRate, productSearchResult, productSearchSelect, productSearchStart } from "./eventNames";
/**
 * Basisklasse für alle Events. Erweitert [CustomEvent]('https://developer.mozilla.org/docs/Web/API/CustomEvent')
 * Objekte der `detail` property sind konform zu dem Typ `T`
 */
export class NamespacedCustomEvent extends CustomEvent {
    constructor(eventNamespace, eventName, detail) {
        super(getEventName(eventNamespace, eventName), { detail: deepClone(detail) });
    }
}
/**
 * Event name: `<eventNamespace>.productSearch.start`  {@link eventNames.productSearchStart}
 *
 * Ausgelöst durch den Start der Produktsuche. Dies kann entweder durch einen Klick auf **Tarif berechnen** passieren oder autmatisch nach Übergabe aller erforderlichen Query-Parameter.
 */
export class ProductSearchStartEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, productSearchStart, detail);
    }
}
/**
 * Event name: `<eventNamespace>.productSearch.result` {@link eventNames.productSearchResult}
 *
 * Ausgelöst, wenn ein Prozess zur Produktsuche beendet wurde
 */
export class ProductSearchResultEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, productSearchResult, detail);
    }
}
/**
 * Event name: `<eventNamespace>.productSearch.select` {@link eventNames.productSearchSelect}
 *
 * Ausgelöst, durch Klick auf "Bestellen" bzw. "Wechseln" bei einem bestimmten Produkt.
 */
export class ProductSelectEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, productSearchSelect, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.enter` {@link eventNames.orderStepperEnter}
 *
 * Wird beim Einstieg in die Neuvertragsbestellung ausgelöst.
 */
export class OrderEnterEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepperEnter, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.change` {@link eventNames.orderStepChange}
 *
 * Wird ausgelöst, wenn sich der angezeigte Schritt der Bestellstrecke geändert hat. Dieses Event tritt auf, sobald der entsprechende Schritt angezeigt wird.
 */
export class OrderStepChangeEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepChange, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.customerAccountSelection.submit` {@link eventNames.orderStepCustomerAccountSelectionSubmit}
 *
 * Wird ausgelöst, wenn die die Entscheidung getroffen wurde, ob die Person einen Portal-Account oder ein bestehendes Vertragsverwältnis mit dem Stadtwerk hat. Falls es in diesem Schritt ausgewählt wurde, enthalten die Parameter auch das ausgewählte Kundenkonto, von dem bestehende Daten übernommen werden.
 * **Hinweis** Bei der Anmeldung mit einem Portal-Account wird die Seite des Bestellformulars neu geladen. Ein {@link OrderEnterEvent} wird dabei erneut gesendet.
 */
export class OrderCustomerAccountSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepCustomerAccountSelectionSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.personalData.submit` {@link eventNames.orderStepPersonalDataSubmit}
 *
 * Wird ausgelöst, wenn alle erforderlichen persönlichen Daten eingegeben wurden.
 */
export class OrderPersonalDataSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepPersonalDataSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.deliveryAddress.submit` {@link eventNames.orderStepDeliveryAddressSubmit}
 *
 * Wird ausgelöst, wenn alle Angaben zur Liefer- bzw. eventuell abweichenden Rechnungsadresse gemacht wurden.
 */
export class OrderDeliveryAddressSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepDeliveryAddressSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.supplyConnection.submit` {@link eventNames.orderStepSupplyConnectionSubmit}
 *
 * Wird ausgelöst, wenn alle benötigten Angaben zur Belieferung gemacht wurden.
 */
export class OrderSupplyConnectionSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepSupplyConnectionSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.paymentMethod.submit` {@link eventNames.orderStepPaymentMethodSubmit}
 *
 * Wird ausgelöst, wenn alle benötigten Angaben zur Zahlungsart gemacht wurden.
 */
export class OrderPaymentMethodSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepPaymentMethodSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.step.approvalFlags.submit` {@link eventNames.orderStepApprovalFlagsSubmit}
 *
 * Wird ausgelöst, wenn alle benötigten Zustimmungen gegeben wurden. Da dieses Event **vor** dem Absenden der Bestellung ausgelöst wird, kann es im Zuge einer Bestellung ggf. mehrfach auftreten (z.B. Wenn die Bestellung fehlgeschlagen ist)
 */
export class OrderApprovalFlagsSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderStepApprovalFlagsSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.submit` {@link eventNames.orderSubmit}
 *
 * Wird ausgelöst, wenn die Bestellung erfolgreich eingegangen ist. Dieses Event kann im Zuge einer Bestellung (mit einer bestimmten `issueId`) nur einmal auftreten.
 */
export class OrderSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.toChangeRate` {@link eventNames.orderToChangeRate}
 *
 * Wird ausgelöst, wenn von der Neuvertragsbestellung zum Tarifwechsel gewechselt wird. Dieses Event kann nur auftreten, wenn die Person ein bestehendes Vertragsverhältnis mit dem Stadtwerk hat.
 */
export class OrderToChangeRateEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderToChangeRate, detail);
    }
}
/**
 * Event name: `<eventNamespace>.order.backToSelection` {@link eventNames.orderBackToSelection}
 *
 * Wird ausgelöst, wenn in der Neuvertragsbestellstrecke zurück zur Produktsuche navigiert wird.
 */
export class OrderBackToSelectionEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, orderBackToSelection, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.enter` {@link eventNames.changeRateStepperEnter}
 *
 * Verhält sich wie {@link OrderEnterEvent}, für einen Tarifwechsel. Durch den Wechsel zwischen Neuvertragsbestellung und Tarifwechsel können diese Events und {@link OrderEnterEvent}s mit der selben `issueId` auftreten
 */
export class ChangeRateEnterEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateStepperEnter, detail);
    }
}
/**
 * Event name: `<eventNamepspace>.changeRate.step.change` {@link eventNames.changeRateStepChange}
 *
 * Siehe {@link OrderStepChangeEvent}
 */
export class ChangeRateStepChangeEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateStepChange, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.step.deliveryDate.submit` {@link eventNames.changeRateStepDeliveryDateSubmit}
 *
 * Wird ausgelöst, wenn alle Angaben zum Wechseltermin bei einem Tarifwechsel gemacht wurden.
 */
export class ChangeRateDeliveryDateSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateStepDeliveryDateSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.step.approvalFlags.submit` {@link eventNames.changeRateStepApprovalFlagsSubmit}
 *
 * Siehe {@link OrderApprovalFlagsSubmitEvent}
 */
export class ChangeRateApprovalFlagsSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateStepApprovalFlagsSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.submit` {@link eventNames.changeRateSubmit}
 *
 * Siehe {@link OrderSubmitEvent}
 */
export class ChangeRateSubmitEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateSubmit, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.backToSelection` {@link eventNames.changeRateBackToSelection}
 *
 * Wird ausgelöst, wenn von einem Tarifwechsel zurück zur Produktsuche navigiert wird.
 */
export class ChangeRateBackToSelectionEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateBackToSelection, detail);
    }
}
/**
 * Event name: `<eventNamespace>.changeRate.backToNewContract` {@link eventNames.changeRateBackToNewContract}
 *
 * Wird ausgelöst, wenn von einem Tarifwechsel zurück zur Neuvertragsbestellung gewechselt wird. Dieses event kann nur auftreten, wenn der Tarifwechsel zurvor über die Neuverragsbestellung gestartet wurde.
 */
export class ChangeRateBackToNewContractEvent extends NamespacedCustomEvent {
    constructor(eventNamespace, detail) {
        super(eventNamespace, changeRateBackToNewContract, detail);
    }
}
//# sourceMappingURL=eventClasses.js.map